import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, Trash2, AlertTriangle, Terminal } from "lucide-react";

export default function IDGLogsTab() {
  const [logs, setLogs] = useState({ stderr: "", system: "" });
  const [loading, setLoading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("stderr");

  const loadLogs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/idg/admin/logs");
      setLogs(data);
      toast.success("Loguri actualizate!");
    } catch (e) {
      toast.error("Eroare la încărcarea logurilor: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const clearLogs = async () => {
    if (!window.confirm("Sigur dorești să golești toate logurile?")) return;
    try {
      await api.delete("/idg/admin/logs");
      setLogs({ stderr: "", system: "" });
      toast.success("Toate logurile au fost golite.");
    } catch (e) {
      toast.error("Eroare la golirea logurilor: " + (e?.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const currentContent = activeSubTab === "stderr" ? logs.stderr : logs.system;

  const renderLogLines = (text) => {
    if (!text) return <p className="text-zinc-500 italic p-4">Fișierul de log este gol.</p>;
    const lines = text.split("\n");
    return (
      <div className="font-mono text-xs overflow-auto max-h-[60vh] bg-black/40 p-4 border border-white/5 space-y-0.5 leading-relaxed text-zinc-300">
        {lines.map((line, idx) => {
          let lineClass = "";
          if (line.toUpperCase().includes("ERROR") || line.toUpperCase().includes("EXCEPTION") || line.toUpperCase().includes("CRITICAL")) {
            lineClass = "text-red-400 bg-red-950/20 px-1 border-l-2 border-red-500";
          } else if (line.toUpperCase().includes("WARN") || line.toUpperCase().includes("WARNING")) {
            lineClass = "text-yellow-400 bg-yellow-950/20 px-1 border-l-2 border-yellow-500";
          }
          return (
            <div key={idx} className={`${lineClass} whitespace-pre-wrap break-all hover:bg-white/5`}>
              <span className="text-zinc-600 select-none mr-3 inline-block w-8 text-right">{idx + 1}</span>
              {line}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="logs-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="tf-heading text-2xl font-bold uppercase tracking-wider flex items-center gap-2">
            <Terminal className="w-6 h-6 text-[var(--tf-primary)]" /> Vizualizator Loguri
          </h2>
          <p className="text-xs text-zinc-500 tf-mono">Consola de erori și monitorizare a sistemului</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={loadLogs} 
            disabled={loading} 
            className="tf-btn-secondary tf-clip-btn text-xs flex items-center gap-1"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Reîncarcă
          </button>
          <button 
            onClick={clearLogs} 
            className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-1 bg-red-600 hover:bg-red-700"
          >
            <Trash2 className="w-3.5 h-3.5" /> Golește Loguri
          </button>
        </div>
      </div>

      <div className="tf-surface tf-clip-card overflow-hidden">
        <header className="px-5 py-3 border-b border-white/5 bg-white/5 flex gap-2">
          <button 
            onClick={() => setActiveSubTab("stderr")}
            className={`px-3 py-1.5 text-xs font-bold tf-clip-btn uppercase ${activeSubTab === "stderr" ? "tf-btn-primary" : "tf-btn-secondary"}`}
          >
            stderr.log (Erori Python)
          </button>
          <button 
            onClick={() => setActiveSubTab("system")}
            className={`px-3 py-1.5 text-xs font-bold tf-clip-btn uppercase ${activeSubTab === "system" ? "tf-btn-primary" : "tf-btn-secondary"}`}
          >
            system.log (Activitate)
          </button>
        </header>

        <div>
          {renderLogLines(currentContent)}
        </div>
      </div>
    </div>
  );
}
